import Service, { service } from '@ember/service'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type { Log } from '@blakeelearning/log'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import type { CreateFeedbackPayload } from 'teacher-ui-client/components/writing-legends/teacher-feedback-form.gts'

export default class WritingLegendsSubmissionService extends Service {
  @service authToken!: AuthToken

  @service log!: Log

  get headers() {
    return {
      Authorization: `Bearer ${this.authToken.token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    }
  }

  buildSubmissionUrl(studentId: string, snapshotId: string): string {
    return join(config.writingLegendsApiUrl, 'v1', 'ewi', studentId, 'snapshots', snapshotId).href
  }

  buildResultUrl(schoolId: string, activityId: string, resultId: string): string {
    return join(
      config.writingLegendsApiUrl,
      'v1',
      'retui',
      'school',
      schoolId,
      'long-writes',
      activityId,
      'results',
      resultId,
    ).href
  }

  buildStudentProfileUrl(studentId: string): string {
    return join(config.writingLegendsApiUrl, 'v1', 'students', studentId).href
  }

  buildFeedbackUrl(studentId: string, resultId: string): string {
    return join(config.writingLegendsApiUrl, 'v1', 'ewi', studentId, 'results', resultId, 'feedbacks').href
  }

  buildFeedbackUrlForPost(studentId: string, resultId: string, snapshotId: string): string {
    return join(
      config.writingLegendsApiUrl,
      'v1',
      'ewi',
      studentId,
      'results',
      resultId,
      'snapshots',
      snapshotId,
      'feedbacks',
    ).href
  }

  async fetchData<R>(url: string): Promise<R | undefined> {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.headers,
      })

      if (response.ok) {
        return (await response.json()) as R
      } else {
        // Some unknown error. Log it.
        this.log.error(`Failed to fetch writing legends submission; Reason: (${response.status})`, url)
      }
    } catch (e: any) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        // Fetch aborted by user action, eg closed tab, stop button, network outage. Do nothing.
      } else {
        this.log.error(`Failed to fetch writing legends submission; Reason: (error thrown)`, e, url)
      }
    }
    // we've done our best by reporting the error(s),
    // the caller should handle an undefined result.
    return undefined
  }

  async submitFeedback(url: string, feedback: CreateFeedbackPayload) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(feedback),
      })

      if (!response.ok) {
        // Some unknown error. Log it.
        this.log.error(`Failed to submit writing legends feedback; Reason: (${response.status})`, url)
      }

      return response.ok
    } catch (e: any) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        // Fetch aborted by user action, eg closed tab, stop button, network outage. Do nothing.
      } else {
        this.log.error(`Failed to submit writing legends feedback; Reason: (error thrown)`, e, url)
      }
    }
    return false
  }
}
declare module '@ember/service' {
  interface Registry {
    writingLegendsSubmission: WritingLegendsSubmissionService
  }
}
